import * as React from "react";

import { cn } from "../lib/utils";

/**
 * Grow a textarea to fit its content.
 *
 * The class list below asks for this declaratively with `field-sizing-content`,
 * but that utility only exists in Tailwind v4 and this package's declared v4
 * is not what builds the app: the dependency resolves to Tailwind v3, which
 * drops the unknown utility silently, so the rule never reaches the browser.
 * Confirmed by grepping the shipped CSS bundle: `min-h-16` from this same class
 * list is present, `field-sizing` appears nowhere. And even once the two
 * Tailwind majors are aligned, `field-sizing` is Chromium-only, which would
 * still leave Safari and Firefox users typing into a fixed box. Measuring here
 * works everywhere, in edit and read-only alike; the class stays so the
 * declarative intent survives.
 *
 * Height is reset to `auto` first so the element can shrink as well as grow;
 * the border delta is added back because `scrollHeight` excludes borders while
 * `height` under border-box sizing includes them.
 */
const resizeToContent = (element: HTMLTextAreaElement | null) => {
  if (!element) {
    return;
  }
  // Hidden (closed dialog, collapsed section): measuring yields 0 and would
  // collapse the box, so leave the height alone. Every call site today lives
  // in a Radix Dialog or Tabs, which unmount rather than hide, so the element
  // is measured on mount. A box kept mounted-but-hidden would need an
  // IntersectionObserver to re-measure on reveal — nothing renders that way
  // today, and typing or a value change re-measures in any case.
  if (element.offsetParent === null && element.scrollHeight === 0) {
    return;
  }
  element.style.height = "auto";
  const borders = element.offsetHeight - element.clientHeight;
  element.style.height = `${element.scrollHeight + borders}px`;
};

/**
 * Keep both the caller's ref and our own pointed at the same node.
 */
const assignRef = (
  ref: React.ForwardedRef<HTMLTextAreaElement>,
  node: HTMLTextAreaElement | null
) => {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref) {
    ref.current = node;
  }
};

export interface TextareaProps extends React.ComponentProps<"textarea"> {
  /**
   * Grow with the content instead of scrolling inside a fixed box.
   *
   * Defaults to ON, except when the call site sets `rows` — a caller that
   * states a row count has sized the box deliberately, and growth would
   * silently override that sizing the moment the box renders. Pass it
   * explicitly to have it either way.
   */
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, autoResize, onInput, ...props },
  ref
) {
  const shouldAutoResize = autoResize === undefined ? props.rows === undefined : autoResize;
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      assignRef(ref, node);
    },
    [ref]
  );

  // Runs on mount and whenever the value changes — `value` covers controlled
  // inputs (including a value arriving from a fetch after first paint) and
  // `defaultValue` covers the uncontrolled first render; typing is handled by
  // onInput below, which also covers uncontrolled edits.
  React.useLayoutEffect(() => {
    if (shouldAutoResize) {
      resizeToContent(innerRef.current);
    }
  }, [shouldAutoResize, props.value, props.defaultValue]);

  // A width change re-wraps the text, which changes the height it needs.
  React.useEffect(() => {
    if (!shouldAutoResize) {
      return undefined;
    }
    const onWindowResize = () => resizeToContent(innerRef.current);
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [shouldAutoResize]);

  const handleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    if (shouldAutoResize) {
      resizeToContent(event.currentTarget);
    }
    onInput?.(event);
  };

  return (
    <textarea
      ref={setRefs}
      data-slot="textarea"
      onInput={handleInput}
      className={cn(
        "text-foreground border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 disabled:bg-input/50 dark:disabled:bg-input/80 rounded-lg border bg-transparent px-2.5 py-2 text-base transition-colors focus-visible:ring-2 aria-invalid:ring-[3px] md:text-sm placeholder:text-muted-foreground flex field-sizing-content min-h-16 w-full outline-none disabled:cursor-not-allowed disabled:opacity-50",
        // A ceiling so one very long entry can never push the rest of a form off
        // the screen; past it the box scrolls as before. Override per call site.
        shouldAutoResize && "max-h-[50vh]",
        className
      )}
      {...props}
    />
  );
});

export { Textarea };
